import { viewerUfNames, type ViewerCandidate } from './viewer-data.js';

export interface LeadSnapshot {
  available: boolean;
  uf: string;
  municipality?: string | null;
  candidates: ViewerCandidate[];
  stats?: { sections_totalized?: number | null; sections_total?: number | null; sections_percent?: number | null };
  updated_at?: { date?: string; time?: string } | string | null;
  publication_range?: { to?: { date?: string; time?: string } };
}
export interface LeadChange { previous: ViewerCandidate; leader: ViewerCandidate; publishedAt: number }
export interface ElectionVictory { winner: ViewerCandidate; publishedAt: number }

export function alertScope(params: URLSearchParams, turno: 1 | 2) {
  const detailed = params.get('visualizacao') !== 'tradicional';
  const cargo = detailed ? (params.get('painel_cargo') ?? params.get('cargo')) === 'governador' ? 'governador' : 'presidente'
    : params.get('cargo') ?? 'presidente';
  const selectedUf = (detailed ? params.get('painel_uf') ?? params.get('uf') ?? params.get('map_uf')
    : params.get('uf') ?? params.get('map_uf'))?.toUpperCase() ?? 'BR';
  const uf = viewerUfNames[selectedUf] ? selectedUf : 'BR';
  const municipio = uf === 'BR' ? '' : detailed && params.has('painel_uf') ? params.get('painel_municipio') ?? '' : params.get('municipio') ?? '';
  return { cargo, uf, municipio, turno, key: `${turno}:${cargo}:${uf}:${municipio}`,
    enabled: (cargo === 'presidente' || cargo === 'governador') && (cargo !== 'governador' || uf !== 'BR') };
}

export function publicationTime(snapshot: LeadSnapshot): number | null {
  const publication = snapshot.publication_range?.to ?? snapshot.updated_at;
  if (typeof publication === 'string') {
    const value = /^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(publication) ? Date.parse(publication) : NaN;
    return Number.isFinite(value) ? value : null;
  }
  const match = publication?.date?.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const time = publication?.time?.match(/^(\d{2}):(\d{2}):(\d{2})$/);
  if (!match || !time) return null;
  const [day, month, year, hour, minute, second] = [Number(match[1]), Number(match[2]), Number(match[3]), Number(time[1]), Number(time[2]), Number(time[3])];
  const value = Date.UTC(year!, month! - 1, day!, hour!, minute!, second!);
  const date = new Date(value);
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month! - 1 || date.getUTCDate() !== day
    || hour! > 23 || minute! > 59 || second! > 59) return null;
  // The election's published clock is Brasília (UTC-3 in 2026).
  return value + 3 * 60 * 60 * 1_000;
}

/** One instance per cargo/turno/UF/municipality. Ballot numbers identify entries
 * only within this exact race, never people across elections or territories. */
export function createLeadChangeTracker() {
  let previous: ViewerCandidate | null = null;
  let latest = -Infinity;
  return {
    observe(snapshot: LeadSnapshot): LeadChange | null {
      const publishedAt = publicationTime(snapshot);
      if (!snapshot.available || publishedAt === null || publishedAt <= latest || snapshot.candidates.length < 2) return null;
      const candidates = snapshot.candidates;
      if (candidates.some((candidate) => !candidate.number || !/^\d+$/.test(candidate.number)
        || !Number.isSafeInteger(candidate.votes) || candidate.votes < 0)
        || new Set(candidates.map((candidate) => candidate.number)).size !== candidates.length) return null;
      const [leader, runnerUp] = [...candidates].sort((a, b) => b.votes - a.votes);
      latest = publishedAt;
      if (!leader || leader.votes === 0 || leader.votes === runnerUp?.votes) return null;
      const change = previous && candidates.some((candidate) => candidate.number === previous?.number)
        && previous.number !== leader.number ? { previous, leader, publishedAt } : null;
      previous = leader;
      return change;
    },
  };
}

/** Observes the national presidential result only. Completion of a state or
 * municipality cannot establish that a president has been elected. */
export function createElectionVictoryTracker() {
  let initialized = false;
  let notified = false;
  let latest = -Infinity;
  return {
    observe(snapshot: LeadSnapshot): ElectionVictory | null {
      const publishedAt = publicationTime(snapshot);
      if (!snapshot.available || snapshot.uf !== 'BR' || snapshot.municipality
        || publishedAt === null || publishedAt <= latest || snapshot.candidates.length < 2) return null;
      const candidates = snapshot.candidates;
      if (candidates.some((candidate) => !candidate.number || !/^\d+$/.test(candidate.number)
        || !Number.isSafeInteger(candidate.votes) || candidate.votes < 0)
        || new Set(candidates.map((candidate) => candidate.number)).size !== candidates.length) return null;

      const totalized = snapshot.stats?.sections_totalized;
      const total = snapshot.stats?.sections_total;
      const hasTotalized = totalized != null;
      const hasTotal = total != null;
      if ((hasTotalized && (!Number.isSafeInteger(totalized) || totalized! < 0))
        || (hasTotal && (!Number.isSafeInteger(total) || total! <= 0))
        || (hasTotalized && hasTotal && totalized! > total!)) return null;
      // Counts prevent a rounded 100.00% from announcing completion early.
      // A supplied but incomplete pair is never replaced with the percentage.
      const complete = hasTotalized || hasTotal
        ? hasTotalized && hasTotal && totalized === total
        : snapshot.stats?.sections_percent === 100;
      const elected = candidates.filter((candidate) => candidate.elected === true);
      const [leader, runnerUp] = [...candidates].sort((a, b) => b.votes - a.votes);
      const winner = complete && elected.length === 1 && leader && leader.votes > 0
        && leader.votes > (runnerUp?.votes ?? 0) && leader.number === elected[0]?.number
        ? leader : null;

      latest = publishedAt;
      if (!initialized) {
        initialized = true;
        // Opening an already concluded election is not a new event, including
        // later vote corrections or a temporary regression in the feed.
        notified = Boolean(winner);
        return null;
      }
      if (!winner || notified) return null;
      notified = true;
      return { winner, publishedAt };
    },
  };
}
